import styled, { css } from 'styled-components';
import { colors } from '../../config/styles';
import { GridBorderState, IColumnDropPosition } from './types';
import { useDndGridInternalContext } from './context';
import {
  DND_GRID_GAP,
  GRID_BORDER_WIDTH,
  INDICATOR_THICKNESS,
} from './constants';
import { useDroppable } from '@dnd-kit/core';
import { useDragResize } from '../../utils/hooks/useDragResize';
import { useRowColumnResizerContext } from './useRowColumnResizer';
import React from 'react';
import { withActionsVisibilityContext } from '../ActionsVisibilityContext';
import { useBorderActions } from './useBorderActions';
import BorderActionsMenu from './BorderActionsMenu';
import { DraggingGlobalStyle } from './DraggingGlobalStyle';
import isNil from 'lodash/isNil';
import { theme } from '../../styles/theme';

const Indicator = styled.div``;

const Container = styled.div<{
  $borderState: GridBorderState;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  user-select: none;

  width: ${GRID_BORDER_WIDTH}px;
  margin: 0 -${(GRID_BORDER_WIDTH + DND_GRID_GAP) / 2}px;

  ${Indicator} {
    width: ${INDICATOR_THICKNESS}px;
    height: 0;
    min-height: 0;

    transition:
      height 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms,
      min-height 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms;
    will-change: height, min-height;
  }

  ${({ $borderState }) =>
    $borderState === GridBorderState.EnabledDroppable &&
    css`
      ${Indicator} {
        background-color: ${theme.colors.mainBlue};
        min-height: 40px;
        height: 75%;
      }
    `}

  ${({ $borderState }) =>
    $borderState === GridBorderState.DisabledDroppable &&
    css`
      ${Indicator} {
        background-color: ${colors.warningRed};
        min-height: 40px;
        height: 75%;
      }
    `}

  ${({ $borderState }) =>
    $borderState === GridBorderState.Draggable &&
    css`
      cursor: col-resize;

      ${Indicator} {
        transition-duration: 75ms;
        background-color: ${theme.colors.mainBlue};
        min-height: 40px;
        height: 100%;
      }
    `}

  ${({ $borderState }) =>
    $borderState === GridBorderState.Muted &&
    css`
      ${Indicator} {
        width: 1px;
        border-left: 1px dashed ${theme.colors.secondary800};
        transition-duration: 75ms;
        min-height: 40px;
        height: 100%;
      }
    `}
`;

interface IProps {
  position: IColumnDropPosition;
}

export const GridVerticalBorder = withActionsVisibilityContext(
  ({ position }: IProps) => {
    const { disabled } = useDndGridInternalContext();
    const { setNodeRef } = useDroppable({
      id: `droppable-cell-${position.rowIndex}-${position.colIndex}`,
      data: position,
      disabled,
    });

    const {
      resizeColumnProgressHandler,
      resizeColumnEndHandler,
      canResizeHorizontally,
    } = useRowColumnResizerContext();

    const { isResizing, handleResizeStart } = useDragResize({
      onResizeProgress: resizeColumnProgressHandler,
      onResizeEnd: resizeColumnEndHandler,
    });

    const canResize = canResizeHorizontally(position.colIndex);

    const { borderState, showActions, hideActions, actionsMenuProps } =
      useBorderActions({
        canResize,
        position,
      });

    return (
      <Container
        ref={setNodeRef}
        $borderState={borderState}
        onMouseDown={
          canResize ? (e) => handleResizeStart(e, position.colIndex) : undefined
        }
        onMouseEnter={showActions}
        onMouseLeave={hideActions}
      >
        {actionsMenuProps.isActionsMenuShown &&
          !isNil(actionsMenuProps.items) && (
            <BorderActionsMenu {...actionsMenuProps} />
          )}
        <Indicator />
        <DraggingGlobalStyle $isDragging={isResizing} />
      </Container>
    );
  },
);
